import { createFeatureSelector, createSelector } from '@ngrx/store';
import {
  CHAPTER_FEATURE_KEY,
  ChapterState,
  chapterAdapter,
} from './chapter.reducer';
import { selectParamBookId } from '../book/book.selectors';
import { selectParamParagraphChapterId } from '../paragraph/paragraph.selectors';
import { selectAllChapterProgresses } from '../chapter-progress/chapter-progress.selectors';
import { selectParagraphsWithProgress } from '../practise/practise.selectors';
import { ChapterWithProgress } from '@lyceo/lyceo-pupil-app/practise/util-models';
import { AutocompleteOption } from '@lyceo/shared/util-models';

export const selectChapterState =
  createFeatureSelector<ChapterState>(CHAPTER_FEATURE_KEY);

const { selectAll } = chapterAdapter.getSelectors();

export const selectAllChapters = createSelector(
  selectChapterState,
  (state: ChapterState) => selectAll(state),
);

export const selectBookParamChapters = createSelector(
  selectAllChapters,
  selectParamBookId,
  (chapters, bookId) => chapters.filter((chapter) => chapter.bookId === bookId),
);

export const selectParagraphParamChapter = createSelector(
  selectAllChapters,
  selectParamParagraphChapterId,
  (chapters, paragraphChapterId) =>
    chapters.find((chapter) => chapter.id === paragraphChapterId),
);

export const selectChaptersWithProgress = createSelector(
  selectBookParamChapters,
  selectAllChapterProgresses,
  selectParagraphsWithProgress,
  (
    chapters,
    chapterProgresses,
    paragraphsWithProgress,
  ): ChapterWithProgress[] =>
    chapters.map((chapter) => {
      return {
        chapter,
        chapterProgress: chapterProgresses.find(
          (chapterProgress) => chapterProgress.chapterId === chapter.id,
        ),
        paragraphs: paragraphsWithProgress.filter(
          (paragraphWithProgress) =>
            paragraphWithProgress.paragraph.chapterId === chapter.id,
        ),
      };
    }),
);

export const selectCombinedChaptersParagraphs = createSelector(
  selectChaptersWithProgress,
  (chapters): AutocompleteOption<string>[] => {
    return chapters
      .reduce<AutocompleteOption<string>[]>((list, chapter) => {
        if (chapter.chapter.name && chapter.paragraphs.length) {
          list.push({
            value: chapter.paragraphs[0].paragraph.id,
            label: chapter.chapter.name,
          });
        }
        for (const paragraph of chapter.paragraphs) {
          if (paragraph.paragraph.name) {
            list.push({
              value: paragraph.paragraph.id,
              label: paragraph.paragraph.name,
            });
          }
        }
        return list;
      }, [])
      .sort((a, b) => a.label.localeCompare(b.label));
  },
);
